import React, { useContext, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
  makeStyles
} from "@material-ui/core";
import {
  Add,
  CallEnd,
  DeleteOutline,
  Headset,
  Mic,
  MicOff,
  VolumeUp
} from "@material-ui/icons";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useVoiceRoom } from "../../context/VoiceRoom/VoiceRoomContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  root: {
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: 6
  },
  title: {
    padding: "8px 12px 2px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },
  roomList: {
    maxHeight: 190,
    overflowY: "auto"
  },
  connected: {
    color: theme.palette.success.main
  },
  controls: {
    margin: "4px 10px",
    padding: "7px 9px",
    borderRadius: 8,
    background: theme.palette.action.hover,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  }
}));

const VoiceRooms = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const {
    rooms,
    currentRoom,
    participants,
    muted,
    loadRooms,
    joinRoom,
    leaveRoom,
    toggleMute
  } = useVoiceRoom();
  const [creating, setCreating] = useState(false);
  const [roomName, setRoomName] = useState("");
  const [roomDescription, setRoomDescription] = useState("");

  const createRoom = async () => {
    try {
      await api.post("/voice-rooms", {
        name: roomName,
        description: roomDescription || null
      });
      setCreating(false);
      setRoomName("");
      setRoomDescription("");
      loadRooms();
    } catch (err) {
      toastError(err);
    }
  };

  const removeRoom = async room => {
    if (!window.confirm(`Remover a sala "${room.name}"?`)) return;
    try {
      await api.delete(`/voice-rooms/${room.id}`);
      loadRooms();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.title}>
        <Typography variant="subtitle2">Salas de voz</Typography>
        {user.profile === "admin" && (
          <Tooltip title="Criar sala">
            <IconButton size="small" onClick={() => setCreating(true)}>
              <Add fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </div>

      <List dense className={classes.roomList}>
        {rooms.map(room => (
          <ListItem
            button
            key={room.id}
            selected={currentRoom?.id === room.id}
            onClick={() => joinRoom(room)}
          >
            <ListItemIcon>
              {currentRoom?.id === room.id ? (
                <VolumeUp className={classes.connected} />
              ) : (
                <Headset />
              )}
            </ListItemIcon>
            <ListItemText
              primary={room.name}
              secondary={`${room.participantCount || 0} conectado(s)`}
            />
            {user.profile === "admin" && room.name !== "Sala geral" && (
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  size="small"
                  onClick={event => {
                    event.stopPropagation();
                    removeRoom(room);
                  }}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            )}
          </ListItem>
        ))}
      </List>

      {currentRoom && (
        <div className={classes.controls}>
          <div>
            <Typography variant="caption" display="block">
              Conectado em {currentRoom.name}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {participants.map(item => item.name).join(", ")}
            </Typography>
          </div>
          <div>
            <IconButton size="small" onClick={toggleMute}>
              {muted ? <MicOff color="error" /> : <Mic />}
            </IconButton>
            <IconButton size="small" onClick={leaveRoom}>
              <CallEnd color="error" />
            </IconButton>
          </div>
        </div>
      )}

      <Dialog
        open={creating}
        onClose={() => setCreating(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Nova sala de voz</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Nome"
            value={roomName}
            onChange={event => setRoomName(event.target.value)}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Descrição"
            value={roomDescription}
            onChange={event => setRoomDescription(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreating(false)}>Cancelar</Button>
          <Button
            color="primary"
            variant="contained"
            disabled={roomName.trim().length < 2}
            onClick={createRoom}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default VoiceRooms;
